"use client";

import { cn } from "@/utils/cn";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Input as AriaInput, Label, Text } from "react-aria-components";

type ArrayInputProps = {
	label?: string;
	name?: string;
	description?: string;
	errorMessage?: string;
	className?: string;
	inputClassName?: string;
	value?: string[];
	onChange?: (value: string[]) => void;
};

export const ArrayInput = ({ label, description, errorMessage, className, value = [], onChange }: ArrayInputProps) => {
	const [items, setItems] = useState<string[]>(Array.isArray(value) && value.length > 0 ? value : [""]);

	const addItem = () => {
		const newItems = [...items, ""];
		setItems(newItems);
		onChange?.(newItems);
	};

	const removeItem = (index: number) => {
		if (index === 0 && items.length === 1) {
			// For the first input when it's the only one, just clear the content
			const newItems = [""];
			setItems(newItems);
			onChange?.(newItems);
		} else {
			// For other inputs, remove the entire row
			const newItems = items.filter((_, i) => i !== index);
			setItems(newItems);
			onChange?.(newItems);
		}
	};

	const clearFirstItem = () => {
		const newItems = items.map((item, i) => (i === 0 ? "" : item));
		setItems(newItems);
		onChange?.(newItems);
	};

	const updateItem = (index: number, newValue: string) => {
		const newItems = items.map((item, i) => (i === index ? newValue : item));
		setItems(newItems);
		onChange?.(newItems);
	};

	useEffect(() => {
		// Deep compare to prevent unnecessary re-renders from new array references
		const currentValues = items.filter(item => item.trim() !== "");
		const newValues = Array.isArray(value) ? value.filter(item => item.trim() !== "") : [];

		if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
			setItems(value.length > 0 ? value : [""]);
		}
	}, [value]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className={cn("flex flex-col py-3 my-3 gap-2", className)}>
			{label && <Label className="text-title-medium">{label}</Label>}

			<div className="space-y-2">
				{items.map((item, index) => {
					const isLastItem = index === items.length - 1;
					const hasContent = item.trim().length > 0;
					const isFirstItem = index === 0;

					return (
						<div key={index} className="flex gap-2 items-center">
							<AriaInput
								value={item}
								onChange={e => updateItem(index, e.target.value)}
								className={cn(
									// Consistent input styling
									"flex-1 border border-gray-300 bg-white px-4 py-3 h-12 rounded-lg text-body placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-primary-600 transition-colors duration-150",
									errorMessage
										? "border-primary-500 focus:ring-primary-600 focus:border-primary-600"
										: "focus:border-primary-600",
								)}
								placeholder={`${index + 1}. `}
							/>
							{/* Always reserve space for icons to prevent layout jumping */}
							<div className="flex gap-1 min-w-[80px]">
								{/* Delete icon - show for first row only when data available, always show for other rows */}
								{(isFirstItem ? hasContent : true) && (
									<button
										type="button"
										onClick={() => {
											if (isFirstItem && items.length === 1) {
												clearFirstItem();
											} else {
												removeItem(index);
											}
										}}
										className="p-2 text-primary-400 hover:text-primary-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
										title={isFirstItem && items.length === 1 ? "Clear" : "Delete"}
									>
										<Trash2 size={16} />
									</button>
								)}
								{/* Plus icon - shown only on the last item when it has content */}
								{isLastItem && hasContent && (
									<button
										type="button"
										onClick={addItem}
										className="p-2 text-grey-400 hover:text-grey-600 hover:bg-grey-50 rounded-lg transition-colors"
										title="Add"
									>
										<Plus size={16} />
									</button>
								)}
							</div>
						</div>
					);
				})}
			</div>

			{description && <Text className="text-body-small">{description}</Text>}

			<Text className="text-body-small-medium text-destructive h-1">{errorMessage || ""}</Text>
		</div>
	);
};
